/**
 * Tool Results Display Component
 * Renders results from MCP tool executions
 */

import React from 'react';
import type {
  ToolResult,
  SearchEntitiesResult,
  CountEntitiesResult,
} from '@/api/types/mcp.types';
import { LeadCard } from './EntityCards/LeadCard';
import { TicketCard } from './EntityCards/TicketCard';
import { BPCard } from './EntityCards/BPCard';
import { CalendarEventCard } from './EntityCards/CalendarEventCard';
import { GenericEntityCard } from './EntityCards/GenericEntityCard';
import { KanbanBoardCard } from './EntityCards/KanbanBoardCard';
import { WorkloadSummary } from './EntityCards/WorkloadSummary';
import { ResultActions } from './ResultActions';
import './ToolResultsDisplay.css';

interface ToolResultsDisplayProps {
  results: ToolResult[];
}

export const ToolResultsDisplay: React.FC<ToolResultsDisplayProps> = ({
  results,
}) => {
  if (!results || results.length === 0) return null;

  return (
    <div className="tool-results">
      {results.map((result, index) => (
        <div key={index} className="tool-result">
          <div className="tool-result-header">
            <span className="tool-name">{result.tool}</span>
          </div>

          {result.error ? (
            <div className="tool-error">
              <span className="error-icon">⚠️</span>
              <span className="error-message">{result.error}</span>
            </div>
          ) : (
            <ResultRenderer tool={result.tool} data={result.result} />
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// Result Renderer (delegates to specific renderers based on tool)
// ============================================================================

interface ResultRendererProps {
  tool: string;
  data: any;
}

const ResultRenderer: React.FC<ResultRendererProps> = ({ tool, data }) => {
  if (!data) return null;

  // Handle Kanban tools
  if (tool === 'list_kanban_boards' && data.boards) {
    return (
      <>
        <KanbanBoardsRenderer boards={data.boards} />
        <ResultActions result={data} toolName={tool} />
      </>
    );
  }

  if (tool === 'find_kanban_board' && data.boards) {
    return (
      <>
        <KanbanBoardsRenderer boards={data.boards} />
        <ResultActions result={data} toolName={tool} />
      </>
    );
  }

  if (tool === 'get_board_with_most_tickets' && data.boards) {
    return (
      <>
        <KanbanBoardsRenderer boards={data.boards} />
        <ResultActions result={data} toolName={tool} />
      </>
    );
  }

  if (tool === 'get_my_kanban_workload') {
    return <WorkloadSummary data={data} />;
  }

  if (tool === 'find_urgent_items_assigned_to_me') {
    return <UrgentItemsRenderer data={data} />;
  }

  // Handle search_entities tool
  if (tool === 'search_entities') {
    return (
      <>
        <SearchEntitiesRenderer data={data as SearchEntitiesResult} />
        <ResultActions result={data} toolName={tool} />
      </>
    );
  }

  // Handle count_entities tool
  if (tool === 'count_entities') {
    return <CountEntitiesRenderer data={data as CountEntitiesResult} />;
  }

  // Handle create/update operations
  if (tool.startsWith('create_') || tool.startsWith('update_')) {
    return <WriteOperationRenderer data={data} />;
  }

  // Generic fallback
  return <GenericRenderer data={data} />;
};

// ============================================================================
// Kanban Boards Renderer
// ============================================================================

const KanbanBoardsRenderer: React.FC<{ boards: any[] }> = ({ boards }) => {
  return (
    <div className="kanban-boards-results">
      {boards.map((board) => (
        <KanbanBoardCard key={board.id} board={board} />
      ))}
    </div>
  );
};

// ============================================================================
// Urgent Items Renderer
// ============================================================================

const UrgentItemsRenderer: React.FC<{ data: any }> = ({ data }) => {
  return (
    <div className="urgent-items">
      {data.urgent && data.urgent.length > 0 && (
        <div className="urgent-section">
          <h5 className="urgent-title">
            🔴 Urgentni Items ({data.urgent.length})
          </h5>
          <div className="urgent-list">
            {data.urgent.map((item: any) => (
              <div key={item.id} className="urgent-item">
                <span className="item-icon">
                  {item.type === 'ticket' ? '🎫' : '👤'}
                </span>
                <span className="item-title">{item.title}</span>
                {item.priority !== undefined && (
                  <span className="item-priority">P{item.priority}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {data.blocked && data.blocked.length > 0 && (
        <div className="blocked-section">
          <h5 className="blocked-title">
            🚫 Blokirani Items ({data.blocked.length})
          </h5>
          <div className="blocked-list">
            {data.blocked.map((item: any) => (
              <div key={item.id} className="blocked-item">
                <span className="item-icon">
                  {item.type === 'ticket' ? '🎫' : '👤'}
                </span>
                <span className="item-title">{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Search Entities Renderer
// ============================================================================

const SearchEntitiesRenderer: React.FC<{ data: SearchEntitiesResult }> = ({
  data,
}) => {
  const { leads, tickets, bps, calendar_events } = data;

  return (
    <div className="search-results">
      {/* Leads */}
      {leads && leads.length > 0 && (
        <div className="entity-section">
          <h4 className="entity-section-title">Leads ({leads.length})</h4>
          <div className="entity-cards">
            {leads.map((lead) => (
              <LeadCard key={lead.id} lead={lead} />
            ))}
          </div>
        </div>
      )}

      {/* Tickets */}
      {tickets && tickets.length > 0 && (
        <div className="entity-section">
          <h4 className="entity-section-title">Tickets ({tickets.length})</h4>
          <div className="entity-cards">
            {tickets.map((ticket) => (
              <TicketCard key={ticket.id} ticket={ticket} />
            ))}
          </div>
        </div>
      )}

      {/* Business Partners */}
      {bps && bps.length > 0 && (
        <div className="entity-section">
          <h4 className="entity-section-title">
            Business Partners ({bps.length})
          </h4>
          <div className="entity-cards">
            {bps.map((bp) => (
              <BPCard key={bp.id} bp={bp} />
            ))}
          </div>
        </div>
      )}

      {/* Calendar Events */}
      {calendar_events && calendar_events.length > 0 && (
        <div className="entity-section">
          <h4 className="entity-section-title">
            Events ({calendar_events.length})
          </h4>
          <div className="entity-cards">
            {calendar_events.map((event) => (
              <CalendarEventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Count Entities Renderer
// ============================================================================

const CountEntitiesRenderer: React.FC<{ data: CountEntitiesResult }> = ({
  data,
}) => {
  return (
    <div className="count-result">
      <div className="count-number">{data.total}</div>
      <div className="count-label">{data.entity_type}</div>
      {data.filters && Object.keys(data.filters).length > 0 && (
        <div className="count-filters">
          {Object.entries(data.filters).map(([key, value]) => (
            <span key={key} className="filter-tag">
              {key}: {String(value)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Write Operation Renderer (create/update results)
// ============================================================================

const WriteOperationRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (data.success === false) {
    return (
      <div className="write-operation-error">
        <span className="error-icon">❌</span>
        <span>{data.error || 'Operation failed'}</span>
      </div>
    );
  }

  return (
    <div className="write-operation-success">
      <span className="success-icon">✅</span>
      <span>{data.message || 'Operation completed successfully'}</span>

      {/* Show created/updated entity */}
      {data.ticket && <TicketCard ticket={data.ticket} compact />}
      {data.lead && <LeadCard lead={data.lead} compact />}
      {data.event && <CalendarEventCard event={data.event} compact />}
    </div>
  );
};

// ============================================================================
// Generic Renderer (fallback)
// ============================================================================

const GenericRenderer: React.FC<{ data: any }> = ({ data }) => {
  if (
    typeof data === 'string' ||
    typeof data === 'number' ||
    typeof data === 'boolean'
  ) {
    return <div className="generic-result">{String(data)}</div>;
  }

  if (Array.isArray(data)) {
    return (
      <div className="generic-array">
        {data.map((item, i) => (
          <GenericEntityCard key={i} data={item} />
        ))}
      </div>
    );
  }

  if (typeof data === 'object' && data !== null) {
    // Filter out formatted_message to avoid duplication
    // (backend sends both structured data and formatted markdown)
    const { formatted_message, ...cleanData } = data;
    return <GenericEntityCard data={cleanData} />;
  }

  return null;
};
